import { useState, useEffect } from "react";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, deleteDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { getFlagEmoji } from "../utils/flagEmoji";
import { formatDate } from "../utils/formatDate";
import { getTier, getNextTier } from "../utils/tiers";
import { isTripCreationBlocked } from "../utils/entitlement";
import { useMonetization } from "../context/MonetizationContext";
import CreateTrip from "./CreateTrip";
import Itinerary from "./Itinerary";
import EditTrip from "./EditTrip";
import LocalTips from "./LocalTips";
import TravelJournal from "./TravelJournal";
import EditProfile from "./EditProfile";
import Expenses from "./Expenses";
import Bookings from "./Bookings";
import { awardPoints } from "./Expenses";
import PostTripFlow from "./PostTripFlow";
import Feedback from "./Feedback";
import Friends from "./Friends";
import UpgradeModal from "../components/UpgradeModal";

function multiCityLabel(trip) {
  return trip.legs?.map((l) => l.city).join(" → ") || trip.destination;
}

function TripCard({ trip, uid, tripMenuOpen, setTripMenuOpen, setDeleteConfirmTrip,
  setSelectedTrip, setSelectedExpenseTrip, setSelectedBookingsTrip,
  setEditingTrip, copyInviteLink, copiedTripId }) {
  return (
    <div className="db-trip-card">
      <div className="db-trip-card-top">
        <div>
          <div className="db-trip-name">
            {trip.name}
            {trip.isMultiCity && (
              <span style={{
                fontSize: 10, fontWeight: 600, background: '#1E2D4A', color: '#F5A623',
                padding: '2px 7px', borderRadius: 100, marginLeft: 7, verticalAlign: 'middle',
                letterSpacing: '0.03em',
              }}>✨ Multi-City</span>
            )}
          </div>
          <div className="db-trip-dest">
            {trip.isMultiCity
              ? (trip.legs?.map(l => getFlagEmoji(l.countryCode)).filter(Boolean).join(' ') || '📍')
              : (getFlagEmoji(trip.countryCode) || '📍')
            } {trip.isMultiCity ? multiCityLabel(trip) : `${trip.destination}, ${trip.country}`}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span className="db-trip-badge">{trip.memberUids?.length > 1 ? `👥 ${trip.memberUids.length}` : "Solo"}</span>
          {trip.createdBy === uid && (
            <div className="db-trip-menu-wrap">
              <button className="db-trip-menu-btn" onClick={e => { e.stopPropagation(); setTripMenuOpen(tripMenuOpen === trip.id ? null : trip.id); }}>⋯</button>
              {tripMenuOpen === trip.id && (
                <div className="db-trip-menu-dropdown">
                  <button className="db-trip-menu-item" onClick={e => { e.stopPropagation(); setTripMenuOpen(null); setDeleteConfirmTrip(trip); }}>🗑 Delete Trip</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="db-trip-meta">
        <span>📅 {formatDate(trip.startDate)} → {formatDate(trip.endDate)}</span>
        <span>🗓 {trip.days} days</span>
        {trip.isMultiCity && trip.legs?.length > 0 && (
          <span>🏙 {trip.legs.length} cities</span>
        )}
      </div>
      {trip.bookings?.length > 0 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
          {trip.bookings.map((b, i) => (
            <span key={i} style={{fontSize:11,padding:'2px 8px',borderRadius:100,background:'#FEF0DC',color:'#C47A0A'}}>
              {b.type === 'flight' ? '✈️' : b.type === 'hotel' ? '🏨' : b.type === 'car_rental' ? '🚗' : '🎭'} {b.name || b.airline || b.hotel}
            </span>
          ))}
        </div>
      )}
      <div className="db-trip-actions">
        <button className="db-trip-action-btn" onClick={() => setSelectedTrip(trip)}>✨ Itinerary</button>
        <button className="db-trip-action-btn" onClick={() => setSelectedExpenseTrip(trip)}>💸 Expenses</button>
        <button className="db-trip-action-btn" onClick={() => setSelectedBookingsTrip(trip)}>🗂️ Bookings</button>
        <button className="db-trip-action-btn" onClick={() => setEditingTrip(trip)}>✏️ Edit</button>
        <button className="db-trip-action-btn" onClick={() => copyInviteLink(trip)}
          style={copiedTripId === trip.id ? {borderColor:'#1D9E75',color:'#1D9E75'} : {}}>
          {copiedTripId === trip.id ? "✓ Copied!" : "🔗 Invite"}
        </button>
      </div>
    </div>
  );
}

export default function Dashboard({ pendingJoinTrip, onPendingJoinHandled }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [creatingTrip, setCreatingTrip] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [copiedTripId, setCopiedTripId] = useState(null);
  const [showLocalTips, setShowLocalTips] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [selectedExpenseTrip, setSelectedExpenseTrip] = useState(null);
  const [selectedBookingsTrip, setSelectedBookingsTrip] = useState(null);
  const [postTripTrip, setPostTripTrip] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [profileTab, setProfileTab] = useState("profile");
  const [loadError, setLoadError] = useState(false);
  const [bookingContextForItinerary, setBookingContextForItinerary] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [tripMenuOpen, setTripMenuOpen] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [deleteConfirmTrip, setDeleteConfirmTrip] = useState(null);
  const [deleteStep2, setDeleteStep2] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const { premiumGateEnabled } = useMonetization();

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (trips.length > 0) {
      const today = new Date().toISOString().split("T")[0];
      const ended = trips.find(t => t.endDate && t.endDate < today && !t[`postTripDone_${uid}`]);
      if (ended) setPostTripTrip(ended);
    }
  }, [trips]);

  const fetchData = async () => {
    if (!auth.currentUser) { setLoadError(true); return; }
    try {
      const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (snap.exists()) setProfile(snap.data());
      const q = query(collection(db, "trips"), where("memberUids", "array-contains", auth.currentUser.uid));
      const tripsSnap = await getDocs(q);
      setTrips(tripsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setLoadError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    if (pendingJoinTrip) {
      setTrips(prev => prev.find(t => t.id === pendingJoinTrip.id) ? prev : [...prev, pendingJoinTrip]);
      setSelectedTrip(pendingJoinTrip);
      onPendingJoinHandled();
      awardPoints(auth.currentUser.uid, 50, "Joined a group trip");
    }
  }, [pendingJoinTrip]);

  const handleSignOut = () => signOut(auth);

  const handleNewTrip = () => {
    const createdTripsCount = trips.filter(t => t.createdBy === auth.currentUser?.uid).length;
    if (isTripCreationBlocked({ premiumGateEnabled, profile, createdTripsCount })) {
      setShowPaywall(true);
    } else {
      setCreatingTrip(true);
    }
  };

  const copyInviteLink = async (trip) => {
    if (!auth.currentUser) return;
    try {
      const isNewToken = !trip.inviteToken;
      const token = trip.inviteToken || crypto.randomUUID();
      const link = `${window.location.origin}/join/${trip.id}?token=${token}`;
      await navigator.clipboard.writeText(link);
      setCopiedTripId(trip.id);
      setTimeout(() => setCopiedTripId(null), 2000);
      if (isNewToken) {
        await updateDoc(doc(db, "trips", trip.id), { inviteToken: token });
        setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, inviteToken: token } : t));
      }
      await awardPoints(auth.currentUser.uid, 25, "Shared trip invite link");
      const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (snap.exists()) setProfile(snap.data());
    } catch (err) {
      console.error("[copyInviteLink]", err);
      setToast("Failed to generate invite link. Please try again.");
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(`https://travelsolis.com/invite/${profile.referralCode}`);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error("[handleCopyReferral]", err);
      setToast("Failed to copy referral link. Please try again.");
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleDeleteTrip = async (trip) => {
    try {
      await deleteDoc(doc(db, "trips", trip.id));
      setTrips(prev => prev.filter(t => t.id !== trip.id));
      setDeleteConfirmTrip(null);
      setDeleteStep2(false);
      setTripMenuOpen(null);
      setToast("Trip deleted");
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Delete trip failed:", err);
      setDeleteConfirmTrip(null);
      setDeleteStep2(false);
      setToast("Failed to delete trip. Please try again.");
      setTimeout(() => setToast(null), 4000);
    }
  };

  useEffect(() => {
    if (!tripMenuOpen) return;
    const close = () => setTripMenuOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [tripMenuOpen]);

  if (loading) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#FAF6F0',
      fontFamily: 'DM Sans, sans-serif',
      gap: 12,
    }}>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="10" fill="#F5A623" opacity="0.9"/>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} .sun-rays{transform-box:view-box;transform-origin:20px 20px;animation:spin 3s linear infinite;}`}</style>
        <g className="sun-rays">
          {[0,45,90,135,180,225,270,315].map((deg, i) => (
            <line key={i} x1="20" y1="5" x2="20" y2="2"
              stroke="#F5A623" strokeWidth="2" strokeLinecap="round"
              transform={`rotate(${deg} 20 20)`}
            />
          ))}
        </g>
      </svg>
      <span style={{color:'rgba(26,23,20,0.35)', fontSize:13}}>Loading your trips...</span>
    </div>
  );

  if (loadError) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#FAF6F0',
      fontFamily: 'DM Sans, sans-serif',
      gap: 12,
      padding: '0 24px',
      textAlign: 'center',
    }}>
      <span style={{fontSize: 32}}>⚠️</span>
      <span style={{fontSize: 15, fontWeight: 500, color: '#1A1714'}}>Couldn't load your trips</span>
      <span style={{fontSize: 13, color: 'rgba(26,23,20,0.5)'}}>Check your connection and try again.</span>
      <button onClick={() => { setLoadError(false); setLoading(true); fetchData(); }} style={{
        marginTop: 8, padding: '10px 24px', background: '#F5A623', color: '#1E2D4A',
        border: 'none', borderRadius: 100, fontSize: 13, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
      }}>Retry</button>
    </div>
  );

  if (creatingTrip) return (
    <CreateTrip
      profile={profile}
      onBack={() => setCreatingTrip(false)}
      onTripCreated={async (trip) => {
        setTrips(prev => [...prev, trip]);
        setCreatingTrip(false);
        setSelectedTrip(trip);
        if (auth.currentUser) await awardPoints(auth.currentUser.uid, 100, "Created a new trip");
        const snap = auth.currentUser ? await getDoc(doc(db, "users", auth.currentUser.uid)) : null;
        if (snap.exists()) setProfile(snap.data());
      }}
    />
  );

  if (editingTrip) return (
    <EditTrip
      trip={editingTrip}
      onBack={() => setEditingTrip(null)}
      onDeleted={() => { setTrips(trips.filter(t => t.id !== editingTrip.id)); setEditingTrip(null); }}
      onUpdated={(updated) => { setTrips(trips.map(t => t.id === updated.id ? updated : t)); setEditingTrip(null); }}
    />
  );

  if (selectedTrip) return (
    <Itinerary
      trip={selectedTrip}
      onBack={() => { setSelectedTrip(null); setBookingContextForItinerary(null); }}
      onEdit={() => { setEditingTrip(selectedTrip); setSelectedTrip(null); setBookingContextForItinerary(null); }}
      onInvite={() => copyInviteLink(selectedTrip)}
      bookingContext={bookingContextForItinerary}
      onItineraryUpdated={() => setBookingContextForItinerary(null)}
    />
  );

  if (selectedExpenseTrip) return (
    <Expenses trip={selectedExpenseTrip} onBack={() => setSelectedExpenseTrip(null)} />
  );

  if (selectedBookingsTrip) return (
    <Bookings
      trip={selectedBookingsTrip}
      onBack={() => setSelectedBookingsTrip(null)}
      onUpdateItinerary={(booking) => {
        const trip = selectedBookingsTrip;
        setBookingContextForItinerary(booking);
        setSelectedBookingsTrip(null);
        setSelectedTrip(trip);
      }}
    />
  );

  if (postTripTrip) return (
    <PostTripFlow
      trip={postTripTrip}
      onComplete={async () => {
        try {
          if (auth.currentUser) await updateDoc(doc(db, "trips", postTripTrip.id), { [`postTripDone_${auth.currentUser.uid}`]: true });
          fetchData();
        } catch (err) {
          console.error("[PostTripFlow.onComplete]", err);
          setToast("Couldn't save your trip wrap-up. Please try again.");
          setTimeout(() => setToast(null), 4000);
        } finally {
          setPostTripTrip(null);
        }
      }}
      onSkip={async () => {
        try {
          if (auth.currentUser) await updateDoc(doc(db, "trips", postTripTrip.id), { [`postTripDone_${auth.currentUser.uid}`]: true });
        } catch (err) {
          console.error("[PostTripFlow.onSkip]", err);
        } finally {
          setPostTripTrip(null);
        }
      }}
    />
  );

  if (showFeedback) return <Feedback onBack={() => setShowFeedback(false)} />;
  if (showLocalTips) return <LocalTips onBack={() => setShowLocalTips(false)} />;
  if (showJournal) return <TravelJournal onBack={() => setShowJournal(false)} />;
  if (editingProfile) return (
    <EditProfile
      onBack={() => setEditingProfile(false)}
      onSaved={(updated) => { setProfile({...profile, ...updated}); setEditingProfile(false); }}
    />
  );

  const firstName = profile?.name?.split(" ")[0] || "Traveler";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const uid = auth.currentUser?.uid;
  const today = new Date().toISOString().split("T")[0];
  const ongoingTrips = trips.filter(t => t.startDate && t.endDate && t.startDate <= today && t.endDate >= today);
  const upcomingTrips = trips.filter(t => t.startDate && t.startDate > today);
  const completedTrips = trips.filter(t => t.endDate && t.endDate < today);

  const cardProps = {
    uid, tripMenuOpen, setTripMenuOpen, setDeleteConfirmTrip,
    setSelectedTrip, setSelectedExpenseTrip, setSelectedBookingsTrip,
    setEditingTrip, copyInviteLink, copiedTripId,
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        body,#root{max-width:100vw;overflow-x:hidden;}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        :root {
          --sol-amber:    #F5A623;
          --sol-amber-lt: #FEF0DC;
          --sol-amber-dk: #C47A0A;
          --sol-navy:     #1E2D4A;
          --sol-navy-lt:  #2C3E5E;
          --sol-blue:     #6BB5D6;
          --sol-blue-lt:  #E0F1F9;
          --sol-cream:    #FAF6F0;
          --sol-cream-dk: #F0E9DE;
          --sol-ink:      #1A1714;
          --sol-ink-40:   rgba(26,23,20,0.4);
          --sol-ink-12:   rgba(26,23,20,0.12);
        }
        .db-root{min-height:100vh;background:var(--sol-cream);font-family:'DM Sans',sans-serif;padding-bottom:80px;}
        .db-nav{background:white;border-bottom:0.5px solid var(--sol-ink-12);padding:14px 20px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;}
        .db-nav-logo{display:flex;align-items:center;gap:8px;}
        .db-nav-logo-mark{width:28px;height:28px;}
        .db-nav-logo-text{font-family:'Playfair Display',serif;font-size:20px;font-weight:600;color:var(--sol-navy);letter-spacing:-0.02em;}
        .db-nav-right{display:flex;align-items:center;gap:10px;}
        .db-points-pill{display:flex;align-items:center;gap:5px;background:var(--sol-amber-lt);border-radius:100px;padding:5px 11px;}
        .db-points-dot{width:7px;height:7px;border-radius:50%;background:var(--sol-amber);}
        .db-points-val{font-size:12px;font-weight:500;color:var(--sol-amber-dk);}
        .db-avatar{width:32px;height:32px;border-radius:50%;background:var(--sol-navy);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:500;color:white;cursor:pointer;overflow:hidden;}
        .db-avatar img{width:100%;height:100%;object-fit:cover;}
        .db-hero{background:var(--sol-navy);padding:28px 20px 44px;position:relative;overflow:hidden;}
        .db-hero::before{content:'';position:absolute;top:-60px;right:-60px;width:260px;height:260px;background:radial-gradient(circle,rgba(245,166,35,0.18) 0%,transparent 70%);pointer-events:none;}
        .db-hero::after{content:'';position:absolute;bottom:-30px;left:-20px;width:180px;height:180px;background:radial-gradient(circle,rgba(107,181,214,0.12) 0%,transparent 70%);pointer-events:none;}
        .db-greeting{font-size:12px;color:rgba(255,255,255,0.45);font-weight:300;margin-bottom:3px;letter-spacing:0.02em;}
        .db-hero-title{font-family:'Playfair Display',serif;font-size:26px;font-weight:600;color:white;letter-spacing:-0.02em;margin-bottom:20px;line-height:1.25;}
        .db-hero-title em{font-style:italic;color:var(--sol-amber);}
        .db-hero-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;}
        .db-stat{background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.09);border-radius:14px;padding:14px;text-align:center;}
        .db-stat-num{font-family:'Playfair Display',serif;font-size:24px;font-weight:600;color:white;margin-bottom:2px;}
        .db-stat-label{font-size:11px;color:rgba(255,255,255,0.4);font-weight:300;}
        .db-content{padding:20px;margin-top:-18px;}
        .db-new-trip{background:var(--sol-amber);border-radius:18px;padding:20px;display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;cursor:pointer;transition:transform 0.2s,box-shadow 0.2s;box-shadow:0 4px 20px rgba(245,166,35,0.35);}
        .db-new-trip:hover{transform:translateY(-2px);box-shadow:0 8px 28px rgba(245,166,35,0.4);}
        .db-new-trip-text h3{font-family:'Playfair Display',serif;font-size:17px;font-weight:600;color:var(--sol-navy);margin-bottom:3px;}
        .db-new-trip-text p{font-size:12px;color:rgba(30,45,74,0.65);font-weight:300;}
        .db-new-trip-btn{width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.3);display:flex;align-items:center;justify-content:center;font-size:22px;color:var(--sol-navy);flex-shrink:0;font-weight:300;}
        .db-section{margin-bottom:22px;}
        .db-section-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;}
        .db-section-title{font-size:14px;font-weight:500;color:var(--sol-ink);}
        .db-section-action{font-size:12px;color:var(--sol-amber-dk);cursor:pointer;background:none;border:none;font-family:'DM Sans',sans-serif;font-weight:500;}
        .db-empty{background:white;border-radius:16px;border:1.5px dashed var(--sol-ink-12);padding:30px;text-align:center;}
        .db-empty-icon{width:50px;height:50px;border-radius:16px;background:var(--sol-amber-lt);display:flex;align-items:center;justify-content:center;margin:0 auto 12px;font-size:22px;}
        .db-empty-title{font-size:15px;font-weight:500;color:var(--sol-ink);margin-bottom:5px;}
        .db-empty-sub{font-size:13px;color:var(--sol-ink-40);font-weight:300;line-height:1.5;margin-bottom:14px;}
        .db-empty-btn{padding:10px 20px;background:var(--sol-navy);color:white;border:none;border-radius:100px;font-size:13px;font-weight:500;cursor:pointer;font-family:'DM Sans',sans-serif;}
        .db-empty-btn:hover{background:var(--sol-navy-lt);}
        .db-trip-card{background:white;border-radius:16px;border:0.5px solid var(--sol-ink-12);padding:16px;margin-bottom:10px;transition:transform 0.15s,box-shadow 0.15s;}
        .db-trip-card:hover{transform:translateY(-2px);box-shadow:0 4px 20px rgba(26,23,20,0.07);}
        .db-trip-card-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;}
        .db-trip-name{font-size:15px;font-weight:500;color:var(--sol-ink);margin-bottom:2px;}
        .db-trip-dest{font-size:12px;color:var(--sol-ink-40);font-weight:300;}
        .db-trip-badge{font-size:11px;font-weight:500;padding:3px 10px;border-radius:100px;background:var(--sol-amber-lt);color:var(--sol-amber-dk);white-space:nowrap;}
        .db-trip-meta{font-size:12px;color:var(--sol-ink-40);display:flex;gap:12px;flex-wrap:wrap;}
        .db-trip-actions{display:flex;gap:6px;margin-top:10px;flex-wrap:wrap;}
        .db-trip-action-btn{font-size:11px;padding:5px 12px;border-radius:100px;border:0.5px solid var(--sol-ink-12);background:var(--sol-cream);color:rgba(26,23,20,0.65);cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.15s;white-space:nowrap;}
        .db-trip-action-btn:hover{border-color:var(--sol-amber);color:var(--sol-amber-dk);}
        .db-feature-card{background:white;border-radius:16px;border:0.5px solid var(--sol-ink-12);padding:18px;cursor:pointer;transition:transform 0.15s,box-shadow 0.15s;margin-bottom:10px;}
        .db-feature-card:hover{transform:translateY(-2px);box-shadow:0 4px 20px rgba(26,23,20,0.07);}
        .db-feature-card-top{display:flex;align-items:center;gap:12px;margin-bottom:7px;}
        .db-feature-icon{width:42px;height:42px;border-radius:12px;background:var(--sol-amber-lt);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
        .db-feature-title{font-size:15px;font-weight:500;color:var(--sol-ink);}
        .db-feature-sub{font-size:13px;color:var(--sol-ink-40);font-weight:300;line-height:1.5;}
        .db-profile-card{background:white;border-radius:16px;border:0.5px solid var(--sol-ink-12);padding:20px;margin-bottom:12px;}
        .db-profile-top{display:flex;align-items:center;gap:14px;margin-bottom:16px;}
        .db-profile-av{width:58px;height:58px;border-radius:50%;background:var(--sol-navy);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:500;color:white;flex-shrink:0;overflow:hidden;}
        .db-profile-av img{width:100%;height:100%;object-fit:cover;}
        .db-profile-info{flex:1;}
        .db-profile-name{font-size:16px;font-weight:500;color:var(--sol-ink);margin-bottom:2px;}
        .db-profile-city{font-size:13px;color:var(--sol-ink-40);font-weight:300;}
        .db-edit-profile-btn{font-size:12px;font-weight:500;color:var(--sol-amber-dk);background:var(--sol-amber-lt);border:none;border-radius:100px;padding:6px 14px;cursor:pointer;font-family:'DM Sans',sans-serif;white-space:nowrap;}
        .db-profile-tags{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px;}
        .db-profile-tag{font-size:11px;padding:3px 10px;border-radius:100px;background:var(--sol-blue-lt);color:#2C6E8A;font-weight:500;}
        .db-profile-row{display:flex;justify-content:space-between;font-size:12px;color:var(--sol-ink-40);padding:6px 0;border-top:0.5px solid rgba(26,23,20,0.06);}
        .db-profile-row span:last-child{font-weight:500;color:var(--sol-ink);text-transform:capitalize;}
        .db-points-card{background:var(--sol-navy);border-radius:16px;padding:20px;margin-bottom:12px;}
        .db-points-title{font-size:11px;color:rgba(255,255,255,0.45);font-weight:300;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.08em;}
        .db-points-big{font-family:'Playfair Display',serif;font-size:36px;font-weight:600;color:white;margin-bottom:3px;}
        .db-points-sub{font-size:12px;color:rgba(255,255,255,0.4);margin-bottom:14px;}
        .db-points-progress-wrap{height:4px;background:rgba(255,255,255,0.1);border-radius:2px;margin-bottom:6px;}
        .db-points-progress{height:4px;background:var(--sol-amber);border-radius:2px;}
        .db-points-labels{display:flex;justify-content:space-between;font-size:11px;color:rgba(255,255,255,0.35);}
        .db-earn-list{margin-top:16px;display:flex;flex-direction:column;gap:8px;}
        .db-earn-row{display:flex;align-items:center;justify-content:space-between;background:rgba(255,255,255,0.05);border-radius:10px;padding:10px 12px;}
        .db-earn-label{font-size:12px;color:rgba(255,255,255,0.6);}
        .db-earn-pts{font-size:12px;font-weight:500;color:var(--sol-amber);}
        .db-feedback-btn{width:100%;padding:13px;border:1.5px solid var(--sol-amber);border-radius:12px;background:white;font-size:14px;font-weight:500;color:var(--sol-amber-dk);cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.2s;margin-top:8px;}
        .db-feedback-btn:hover{background:var(--sol-amber-lt);}
        .db-signout{width:100%;padding:13px;border:1.5px solid var(--sol-ink-12);border-radius:12px;background:white;font-size:14px;font-weight:500;color:var(--sol-ink-40);cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.2s;margin-top:8px;}
        .db-signout:hover{border-color:#c9622f;color:#c9622f;}
        .db-referral-card{background:white;border-radius:16px;border:0.5px solid var(--sol-ink-12);padding:16px;margin-bottom:12px;}
        .db-referral-label{font-size:11px;font-weight:500;color:rgba(26,23,20,0.45);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:10px;}
        .db-referral-row{display:flex;align-items:center;gap:10px;}
        .db-referral-link{flex:1;font-size:11px;color:rgba(26,23,20,0.6);word-break:break-all;font-family:monospace;background:#FAF6F0;border-radius:8px;padding:8px 10px;line-height:1.4;}
        .db-referral-copy{padding:8px 14px;background:#F5A623;color:white;border:none;border-radius:10px;font-size:12px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;white-space:nowrap;transition:background 0.2s;flex-shrink:0;}
        .db-referral-copy:hover{background:#1E2D4A;}
        .db-bottom-nav{position:fixed;bottom:0;left:0;right:0;width:100%;background:white;border-top:0.5px solid var(--sol-ink-12);display:flex;padding:10px 0 20px;z-index:100;overflow:hidden;}
        .db-nav-item{flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;gap:4px;cursor:pointer;padding:4px 0;background:none;border:none;font-family:'DM Sans',sans-serif;}
        .db-nav-icon{width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;}
        .db-nav-icon.active{background:var(--sol-amber-lt);}
        .db-nav-dot{width:10px;height:10px;border-radius:50%;background:rgba(26,23,20,0.18);}
        .db-nav-dot.active{background:var(--sol-amber);}
        .db-nav-label{font-size:10px;color:var(--sol-ink-40);}
        .db-nav-label.active{color:var(--sol-amber-dk);font-weight:500;}
        .db-ongoing-wrap{box-shadow:0 0 0 2px rgba(245,166,35,0.5),0 4px 20px rgba(245,166,35,0.15);border-radius:16px;margin-bottom:10px;}
        .db-ongoing-wrap .db-trip-card{margin-bottom:0;}
        .db-completed-wrap{opacity:0.92;}
        .db-completed-toggle{display:flex;align-items:center;justify-content:space-between;width:100%;background:none;border:none;padding:10px 0 8px;cursor:pointer;font-family:'DM Sans',sans-serif;}
        .db-completed-toggle-title{font-size:14px;font-weight:500;color:var(--sol-ink-40);}
        .db-completed-chevron{font-size:11px;color:var(--sol-ink-40);}
        .db-trip-menu-wrap{position:relative;}
        .db-trip-menu-btn{background:none;border:none;font-size:18px;color:rgba(26,23,20,0.35);cursor:pointer;padding:2px 6px;border-radius:6px;line-height:1;font-family:'DM Sans',sans-serif;}
        .db-trip-menu-btn:hover{background:rgba(26,23,20,0.06);color:rgba(26,23,20,0.6);}
        .db-trip-menu-dropdown{position:absolute;right:0;top:calc(100% + 4px);background:white;border:1px solid rgba(26,23,20,0.1);border-radius:10px;box-shadow:0 8px 24px rgba(26,23,20,0.12);z-index:50;overflow:hidden;min-width:130px;}
        .db-trip-menu-item{display:block;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#c9622f;cursor:pointer;text-align:left;font-family:'DM Sans',sans-serif;transition:background 0.1s;}
        .db-trip-menu-item:hover{background:#fef0ed;}
      `}</style>

      <div className="db-root">
        <div className="db-nav">
          <div className="db-nav-logo">
            <svg className="db-nav-logo-mark" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="7" fill="#F5A623"/>
              {[0,45,90,135,180,225,270,315].map((deg,i)=>(
                <line key={i} x1="14" y1="4" x2="14" y2="1.5"
                  stroke="#F5A623" strokeWidth="1.8" strokeLinecap="round"
                  transform={`rotate(${deg} 14 14)`}/>
              ))}
            </svg>
            <span className="db-nav-logo-text">Solis</span>
          </div>
          <div className="db-nav-right">
            <div className="db-points-pill">
              <div className="db-points-dot"></div>
              <span className="db-points-val">{profile?.points || 0} pts</span>
            </div>
            <div className="db-avatar" onClick={() => setActiveTab("profile")}>
              {profile?.photoURL ? <img src={profile.photoURL} alt="" /> : firstName.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        {activeTab === "home" && (
          <>
            <div className="db-hero">
              <p className="db-greeting">{greeting},</p>
              <h1 className="db-hero-title">{firstName} 👋<br /><em>Where to next?</em></h1>
              <div className="db-hero-stats">
                <div className="db-stat"><div className="db-stat-num">{trips.length}</div><div className="db-stat-label">Trips planned</div></div>
                <div className="db-stat"><div className="db-stat-num">{[...new Set(trips.map(t=>t.country).filter(Boolean))].length}</div><div className="db-stat-label">Countries</div></div>
                <div className="db-stat"><div className="db-stat-num">{profile?.points||0}</div><div className="db-stat-label">Points</div></div>
              </div>
            </div>
            <div className="db-content">
              <div className="db-new-trip" onClick={handleNewTrip}>
                <div className="db-new-trip-text"><h3>Plan a new trip</h3><p>Let AI build your perfect group itinerary</p></div>
                <div className="db-new-trip-btn">+</div>
              </div>
              <div className="db-section">
                {trips.length === 0 ? (
                  <>
                    <div className="db-section-head">
                      <span className="db-section-title">Upcoming trips</span>
                    </div>
                    <div className="db-empty">
                      <div className="db-empty-icon">✈️</div>
                      <div className="db-empty-title">No trips yet</div>
                      <div className="db-empty-sub">Start planning your first group adventure.</div>
                      <button className="db-empty-btn" onClick={handleNewTrip}>Plan my first trip</button>
                    </div>
                  </>
                ) : (
                  <>
                    {ongoingTrips.length > 0 && (
                      <div style={{marginBottom:18}}>
                        <div className="db-section-head" style={{marginBottom:8}}>
                          <span className="db-section-title">🌍 Happening Now</span>
                        </div>
                        {ongoingTrips.map(trip => (
                          <div key={trip.id} className="db-ongoing-wrap">
                            <TripCard trip={trip} {...cardProps} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="db-section-head">
                      <span className="db-section-title">Upcoming trips</span>
                      <button className="db-section-action" onClick={() => setActiveTab("trips")}>See all</button>
                    </div>
                    {upcomingTrips.length === 0 ? (
                      <div className="db-empty">
                        <div className="db-empty-icon">✈️</div>
                        <div className="db-empty-title">No upcoming trips</div>
                        <div className="db-empty-sub">Plan your next adventure! ✈️</div>
                        <button className="db-empty-btn" onClick={handleNewTrip}>Plan a trip</button>
                      </div>
                    ) : upcomingTrips.slice(0, 3).map(trip => <TripCard key={trip.id} trip={trip} {...cardProps} />)}
                    {completedTrips.length > 0 && (
                      <div style={{marginTop:18}}>
                        <button className="db-completed-toggle" onClick={() => setShowCompleted(s => !s)}>
                          <span className="db-completed-toggle-title">Completed Trips ✓ ({completedTrips.length})</span>
                          <span className="db-completed-chevron">{showCompleted ? "▼" : "▶"}</span>
                        </button>
                        {showCompleted && completedTrips.map(trip => (
                          <div key={trip.id} className="db-completed-wrap">
                            <TripCard trip={trip} {...cardProps} />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="db-section">
                <div className="db-section-head"><span className="db-section-title">Explore</span></div>
                <div className="db-feature-card" onClick={() => setShowLocalTips(true)}>
                  <div className="db-feature-card-top"><div className="db-feature-icon">📍</div><div className="db-feature-title">Local tips</div></div>
                  <div className="db-feature-sub">Real tips from travelers and locals. Share yours and earn points.</div>
                </div>
                <div className="db-feature-card" onClick={() => setShowJournal(true)}>
                  <div className="db-feature-card-top"><div className="db-feature-icon">📓</div><div className="db-feature-title">Travel journal & scratch map</div></div>
                  <div className="db-feature-sub">Log memories and track every country you've visited.</div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "trips" && (
          <div className="db-content" style={{paddingTop:28}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
              <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,fontWeight:600,color:'var(--sol-ink)',letterSpacing:'-0.02em'}}>My trips</h2>
              <button className="db-empty-btn" onClick={handleNewTrip}>+ New trip</button>
            </div>
            {trips.length === 0 ? (
              <div className="db-empty">
                <div className="db-empty-icon">🗺️</div>
                <div className="db-empty-title">No trips planned yet</div>
                <div className="db-empty-sub">Create your first trip and invite your group.</div>
                <button className="db-empty-btn" onClick={handleNewTrip}>+ Plan a trip</button>
              </div>
            ) : (
              <>
                {ongoingTrips.length > 0 && (
                  <div style={{marginBottom:18}}>
                    <div style={{fontSize:14,fontWeight:500,color:'var(--sol-ink)',marginBottom:10}}>🌍 Happening Now</div>
                    {ongoingTrips.map(trip => (
                      <div key={trip.id} className="db-ongoing-wrap">
                        <TripCard trip={trip} {...cardProps} />
                      </div>
                    ))}
                  </div>
                )}
                {upcomingTrips.length > 0 && (
                  <div style={{marginBottom:18}}>
                    {(ongoingTrips.length > 0 || completedTrips.length > 0) && (
                      <div style={{fontSize:14,fontWeight:500,color:'var(--sol-ink)',marginBottom:10}}>Upcoming</div>
                    )}
                    {upcomingTrips.map(trip => <TripCard key={trip.id} trip={trip} {...cardProps} />)}
                  </div>
                )}
                {upcomingTrips.length === 0 && ongoingTrips.length === 0 && completedTrips.length > 0 && (
                  <div className="db-empty" style={{marginBottom:18}}>
                    <div className="db-empty-icon">✈️</div>
                    <div className="db-empty-title">No upcoming trips</div>
                    <div className="db-empty-sub">Plan your next adventure! ✈️</div>
                    <button className="db-empty-btn" onClick={handleNewTrip}>Plan a trip</button>
                  </div>
                )}
                {completedTrips.length > 0 && (
                  <div>
                    <button className="db-completed-toggle" onClick={() => setShowCompleted(s => !s)}>
                      <span className="db-completed-toggle-title">Completed Trips ✓ ({completedTrips.length})</span>
                      <span className="db-completed-chevron">{showCompleted ? "▼" : "▶"}</span>
                    </button>
                    {showCompleted && completedTrips.map(trip => (
                      <div key={trip.id} className="db-completed-wrap">
                        <TripCard trip={trip} {...cardProps} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "discover" && (
          <div className="db-content" style={{paddingTop:28}}>
            <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,fontWeight:600,color:'var(--sol-ink)',marginBottom:5,letterSpacing:'-0.02em'}}>Discover</h2>
            <p style={{fontSize:13,color:'var(--sol-ink-40)',fontWeight:300,marginBottom:22}}>Tips from people who actually live there</p>
            <div className="db-feature-card" onClick={() => setShowLocalTips(true)}>
              <div className="db-feature-card-top"><div className="db-feature-icon">📍</div><div className="db-feature-title">Browse local tips</div></div>
              <div className="db-feature-sub">Food, culture, hidden gems and more. Share yours and earn points.</div>
            </div>
          </div>
        )}

        {activeTab === "journal" && (
          <div className="db-content" style={{paddingTop:28}}>
            <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,fontWeight:600,color:'var(--sol-ink)',marginBottom:5,letterSpacing:'-0.02em'}}>Journal</h2>
            <p style={{fontSize:13,color:'var(--sol-ink-40)',fontWeight:300,marginBottom:22}}>Your travel memories and scratch map</p>
            <div className="db-feature-card" onClick={() => setShowJournal(true)}>
              <div className="db-feature-card-top"><div className="db-feature-icon">📓</div><div className="db-feature-title">Open travel journal</div></div>
              <div className="db-feature-sub">Log entries with photos and moods. Track countries on your scratch map.</div>
            </div>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="db-content" style={{paddingTop:28}}>
            <h2 style={{fontFamily:'Playfair Display,serif',fontSize:24,fontWeight:600,color:'var(--sol-ink)',marginBottom:16,letterSpacing:'-0.02em'}}>
              {profileTab === "friends" ? "Friends" : "My profile"}
            </h2>

            <div style={{display:'flex',gap:8,marginBottom:20}}>
              {[{id:"profile",label:"My Profile"},{id:"friends",label:"👥 Friends"}].map(t => (
                <button key={t.id} onClick={() => setProfileTab(t.id)} style={{
                  padding:'8px 18px', borderRadius:100, fontSize:13, fontWeight:500,
                  border: profileTab===t.id ? 'none' : '1.5px solid var(--sol-ink-12)',
                  background: profileTab===t.id ? 'var(--sol-navy)' : 'white',
                  color: profileTab===t.id ? 'white' : 'var(--sol-ink-40)',
                  cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'all 0.15s',
                }}>{t.label}</button>
              ))}
            </div>

            {profileTab === "friends" && (
              <Friends
                profile={profile}
                trips={trips}
                copyInviteLink={copyInviteLink}
                copiedTripId={copiedTripId}
              />
            )}

            {profileTab === "profile" && (
              <>
                <div className="db-profile-card">
                  <div className="db-profile-top">
                    <div className="db-profile-av">
                      {profile?.photoURL ? <img src={profile.photoURL} alt="" /> : firstName.charAt(0).toUpperCase()}
                    </div>
                    <div className="db-profile-info">
                      <div className="db-profile-name">{profile?.name}</div>
                      {profile?.username && <div style={{fontSize:12,color:'var(--sol-ink-40)',fontWeight:300}}>@{profile.username}</div>}
                      <div className="db-profile-city">📍 {profile?.city}</div>
                    </div>
                    <button className="db-edit-profile-btn" onClick={() => setEditingProfile(true)}>Edit ✏️</button>
                  </div>

                  {profile?.vibeLabel && (
                    <div style={{display:'flex',alignItems:'center',gap:10,background:'var(--sol-navy)',borderRadius:12,padding:'12px 14px',marginBottom:14}}>
                      <span style={{fontSize:24}}>{profile.vibeEmoji}</span>
                      <div>
                        <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>Your traveler type</div>
                        <div style={{fontSize:14,fontWeight:500,color:'white'}}>{profile.vibeLabel}</div>
                      </div>
                    </div>
                  )}

                  {profile?.bio && <p style={{fontSize:13,color:'rgba(26,23,20,0.6)',lineHeight:1.6,marginBottom:14,fontWeight:300}}>{profile.bio}</p>}
                  {profile?.interests?.length > 0 && (
                    <div className="db-profile-tags">
                      {profile.interests.slice(0,6).map(i => <span key={i} className="db-profile-tag">{i}</span>)}
                    </div>
                  )}
                  <div className="db-profile-row"><span>Travel style</span><span>{profile?.travelStyle}</span></div>
                  <div className="db-profile-row"><span>Pace</span><span>{profile?.pace}</span></div>
                  <div className="db-profile-row"><span>Diet</span><span>{profile?.food}</span></div>
                  <div className="db-profile-row"><span>Budget</span><span>{profile?.budget}</span></div>
                </div>

                {profile?.referralCode && (
                  <div className="db-referral-card">
                    <div className="db-referral-label">Your Referral Link</div>
                    <div className="db-referral-row">
                      <span className="db-referral-link">https://travelsolis.com/invite/{profile.referralCode}</span>
                      <button className="db-referral-copy" onClick={handleCopyReferral}>
                        {referralCopied ? "✓ Copied!" : "Copy Link"}
                      </button>
                    </div>
                  </div>
                )}

                {(() => {
                  const pts = profile?.points || 0;
                  const isFounder = profile?.isFounder || false;
                  const tier = getTier(pts, isFounder);
                  const next = isFounder ? null : getNextTier(pts);
                  const pct = next
                    ? Math.min(Math.max(((pts - tier.min) / (next.min - tier.min)) * 100, 0), 100)
                    : 100;
                  return (
                <div className="db-points-card">
                  <div className="db-points-title">Your points</div>
                  <div className="db-points-big">{pts}</div>
                  <div className="db-points-sub">
                    {tier.badge} {tier.name}
                    {next ? ` · ${next.min - pts} pts to ${next.name}` : " · Top tier"}
                  </div>
                  <div className="db-points-progress-wrap">
                    <div className="db-points-progress" style={{width:`${pct}%`}}></div>
                  </div>
                  <div className="db-points-labels">
                    <span>{pts} pts</span>
                    {next && <span>{next.min.toLocaleString()} pts</span>}
                  </div>
                  <div className="db-earn-list">
                    <div className="db-earn-row"><span className="db-earn-label">Create a trip</span><span className="db-earn-pts">+100 pts</span></div>
                    <div className="db-earn-row"><span className="db-earn-label">Share invite link</span><span className="db-earn-pts">+25 pts</span></div>
                    <div className="db-earn-row"><span className="db-earn-label">Join a group trip</span><span className="db-earn-pts">+50 pts</span></div>
                    <div className="db-earn-row"><span className="db-earn-label">Add an expense</span><span className="db-earn-pts">+10 pts</span></div>
                    <div className="db-earn-row"><span className="db-earn-label">Share a local tip</span><span className="db-earn-pts">+50 pts</span></div>
                  </div>
                </div>
                  );
                })()}

                <button className="db-feedback-btn" onClick={() => setShowFeedback(true)}>💬 Send feedback</button>
                <button className="db-signout" onClick={handleSignOut}>Sign out</button>
              </>
            )}
          </div>
        )}

        <div className="db-bottom-nav">
          {[{id:"home",label:"Home"},{id:"trips",label:"Trips"},{id:"discover",label:"Discover"},{id:"journal",label:"Journal"},{id:"profile",label:"Profile"}].map(tab => (
            <button key={tab.id} className="db-nav-item" onClick={() => setActiveTab(tab.id)}>
              <div className={`db-nav-icon ${activeTab===tab.id?"active":""}`}>
                <div className={`db-nav-dot ${activeTab===tab.id?"active":""}`}></div>
              </div>
              <span className={`db-nav-label ${activeTab===tab.id?"active":""}`}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {deleteConfirmTrip && (
        <div style={{position:'fixed',inset:0,background:'rgba(26,23,20,0.5)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}} onClick={e => { if (e.target === e.currentTarget) { setDeleteConfirmTrip(null); setDeleteStep2(false); } }}>
          <div style={{background:'white',borderRadius:20,padding:28,maxWidth:400,width:'100%',boxShadow:'0 20px 60px rgba(26,23,20,0.2)'}}>
            {!deleteStep2 ? (
              <>
                <div style={{fontSize:17,fontWeight:600,color:'#1A1714',marginBottom:10,fontFamily:"'Playfair Display',serif"}}>Delete trip?</div>
                <div style={{fontSize:14,color:'rgba(26,23,20,0.6)',lineHeight:1.6,marginBottom:20,fontWeight:300}}>
                  Are you sure you want to delete <strong>{deleteConfirmTrip.name}</strong>? This will permanently remove all itinerary, expenses, bookings, and journal entries.
                </div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => { setDeleteConfirmTrip(null); setDeleteStep2(false); }} style={{flex:1,padding:'12px',border:'1.5px solid rgba(26,23,20,0.12)',borderRadius:12,background:'white',fontSize:14,fontWeight:500,color:'rgba(26,23,20,0.5)',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Cancel</button>
                  <button onClick={() => setDeleteStep2(true)} style={{flex:1,padding:'12px',border:'none',borderRadius:12,background:'#1E2D4A',fontSize:14,fontWeight:500,color:'white',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Yes, delete it</button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:17,fontWeight:600,color:'#c9622f',marginBottom:10,fontFamily:"'Playfair Display',serif"}}>This cannot be undone</div>
                <div style={{fontSize:14,color:'rgba(26,23,20,0.6)',lineHeight:1.6,marginBottom:20,fontWeight:300}}>
                  <strong>{deleteConfirmTrip.name}</strong> and all its data will be permanently deleted. There is no way to recover it.
                </div>
                <div style={{display:'flex',gap:10}}>
                  <button onClick={() => setDeleteStep2(false)} style={{flex:1,padding:'12px',border:'1.5px solid rgba(26,23,20,0.12)',borderRadius:12,background:'white',fontSize:14,fontWeight:500,color:'rgba(26,23,20,0.5)',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Back</button>
                  <button onClick={() => handleDeleteTrip(deleteConfirmTrip)} style={{flex:1,padding:'12px',border:'none',borderRadius:12,background:'#c9622f',fontSize:14,fontWeight:500,color:'white',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Delete Forever</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',background:'#1E2D4A',color:'white',fontSize:13,fontWeight:500,padding:'10px 20px',borderRadius:100,zIndex:400,whiteSpace:'nowrap',boxShadow:'0 4px 20px rgba(26,23,20,0.2)'}}>
          {toast}
        </div>
      )}

      <UpgradeModal open={showPaywall} onClose={() => setShowPaywall(false)} />
    </>
  );
}